import { ImageUp, Type } from 'lucide-react'
import { specEntries, useOptionGroups } from '@/lib/options'
import { lineTotal, money, priceRowLabel } from '@/lib/money'
import { itemArtworks } from '@/lib/artwork'
import { cn } from '@/lib/cn'
import type { Order, OrderItem } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import ProductImage from '@/components/order/ProductImage'
import ArtworkGallery from '@/components/order/ArtworkGallery'
import NameList from '@/components/order/NameList'

/**
 * An order's lines and totals, one layout for the team consoles and the
 * customer's own order page so the two cannot drift apart.
 *
 * `customer` keeps what that page always said differently: the rush share is
 * spelled out per piece, the remark is left off, and Subtotal / Shipping only
 * appear when there is a delivery fee to explain the total.
 */
export default function OrderItemsCard({
  order,
  variant,
  editArtwork,
  editFont,
}: {
  order: Order
  variant: 'team' | 'customer'
  /**
   * Team only: the per-item "Edit artwork" button. `allowed` and `reason`
   * come from the server's edit rule; the button shows the reason when off.
   */
  editArtwork?: { allowed: boolean; reason?: string | null; onEdit: (item: OrderItem) => void }
  /** Team only: the per-item "Edit font" button, same rule as editArtwork. */
  editFont?: { allowed: boolean; reason?: string | null; onEdit: (item: OrderItem) => void }
}) {
  const optionGroups = useOptionGroups()
  const customer = variant === 'customer'
  const showBreakdown = !customer || (order.delivery_fee ?? 0) > 0

  return (
    <Card>
      <SectionLabel>Order details</SectionLabel>
      <div className="hidden grid-cols-[minmax(0,1fr)_6rem_3rem_6.5rem] gap-2 border-b border-line pb-2 text-2xs font-semibold uppercase tracking-wide text-muted sm:grid">
        <span>Item</span>
        <span className="text-right">Price</span>
        <span className="text-right">Qty</span>
        <span className="text-right">Total</span>
      </div>
      <div className="divide-y divide-line">
        {order.items?.map((item) => {
          const rush = (item.urgent_surcharge ?? 0) > 0 && (
            <span className="block text-2xs text-status-danger">
              {customer ? `includes urgent service +${money(item.urgent_surcharge!)}/pc` : `incl. rush +${money(item.urgent_surcharge!)}`}
            </span>
          )
          // Team only: the server sends the system price just to admin and
          // sales, and only on a line whose price was typed by hand. A line
          // agreed before 2026-10-14 was typed per piece and has no total.
          const agreed = !customer && item.computed_unit_price != null && (
            <span className="block text-2xs text-warning" title={item.price_set_by_name ? `Set by ${item.price_set_by_name}` : undefined}>
              {item.agreed_total != null ? `agreed ${money(item.agreed_total)} total` : 'agreed price'} (system {money(item.computed_unit_price)}/pc)
            </span>
          )
          // A line whose sizes were priced apart (Kids 22-32) shows each price.
          const breakdown = item.price_breakdown && item.price_breakdown.length > 1 ? item.price_breakdown : null
          const fonts = item.fonts ?? []
          const hasNames = (item.personalizations?.length ?? 0) > 0
          return (
            <div key={item.id} className="grid gap-2 py-3 text-sm sm:grid-cols-[minmax(0,1fr)_6rem_3rem_6.5rem]">
              <div className="flex min-w-0 gap-3">
                <ProductImage
                  product={{ name: item.product, image_url: item.image_url, image_color: item.image_color }}
                  className="size-12 shrink-0 border border-line"
                />
                <div className="min-w-0">
                  <p className="font-semibold">{item.product}</p>
                  {specEntries(item.spec, optionGroups.data, customer ? ['remark'] : []).map((entry) => (
                    <p key={entry.key} className={cn('text-xs', entry.key === 'remark' ? 'font-semibold text-danger' : 'text-muted')}>
                      {entry.label}: {entry.value}
                    </p>
                  ))}
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {Object.entries(item.size_matrix).map(([size, qty]) => (
                      <span key={size} className="rounded-md bg-foreground/5 px-2 py-0.5 text-xs text-muted">
                        {size}: <span className="font-semibold text-ink">{qty}</span>
                      </span>
                    ))}
                  </div>
                  {/* Every file in order (front, back, ...) as thumbnails
                      that open a preview with the original to download. */}
                  <ArtworkGallery files={itemArtworks(item)} />
                  {editArtwork && (
                    <button
                      type="button"
                      disabled={!editArtwork.allowed}
                      title={editArtwork.allowed ? undefined : (editArtwork.reason ?? undefined)}
                      onClick={() => editArtwork.onEdit(item)}
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:underline disabled:cursor-not-allowed disabled:text-muted disabled:no-underline"
                    >
                      <ImageUp className="size-3.5" />
                      {itemArtworks(item).length > 0 ? 'Edit artwork' : 'Add artwork'}
                    </button>
                  )}
                  {/* The font behind the names and numbers, for the floor's
                      size layout. Only a line with names can need one. */}
                  {(fonts.length > 0 || hasNames) && (
                    <div className="mt-2">
                      <p className="text-2xs font-semibold uppercase tracking-wide text-muted">Font file</p>
                      {fonts.length > 0 ? (
                        <ArtworkGallery files={fonts} />
                      ) : (
                        <p className="text-xs text-muted">No font file sent.</p>
                      )}
                      {editFont && (
                        <button
                          type="button"
                          disabled={!editFont.allowed}
                          title={editFont.allowed ? undefined : (editFont.reason ?? undefined)}
                          onClick={() => editFont.onEdit(item)}
                          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:underline disabled:cursor-not-allowed disabled:text-muted disabled:no-underline"
                        >
                          <Type className="size-3.5" />
                          {fonts.length > 0 ? 'Edit font' : 'Add font'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <p className="hidden text-right text-muted sm:block">
                {breakdown
                  ? breakdown.map((row) => (
                      <span key={row.group ?? ''} className="block">
                        {row.group ?? 'Standard'} {money(row.unit_price)}
                      </span>
                    ))
                  : money(item.unit_price)}
                {rush}
                {agreed}
              </p>
              <p className="hidden text-right sm:block">{item.qty}</p>
              {/* Always priced on the admin, sales and customer order pages;
                  the optional type is for the staff and partner payloads,
                  which omit money and never reach this card. */}
              <p className="hidden text-right font-semibold sm:block">{money(lineTotal(item))}</p>
              <p className="sm:hidden">
                {breakdown
                  ? breakdown.map((row) => (
                      <span key={row.group ?? ''} className="block">
                        {priceRowLabel({ group: row.group, qty: row.qty, price: row.unit_price })}
                      </span>
                    ))
                  : `${item.qty} pcs x ${money(item.unit_price)}`}{' '}
                ={' '}
                <span className="font-semibold">{money(lineTotal(item))}</span>
                {rush}
                {agreed}
              </p>
              {/* Full row width, under the price columns too, so a long list
                  gets several columns instead of one narrow one. */}
              <NameList
                rows={item.personalizations ?? []}
                sizes={Object.keys(item.size_matrix)}
                className="sm:col-span-4 sm:pl-15"
              />
            </div>
          )
        })}
      </div>
      <div className="space-y-1.5 border-t border-line pt-3 text-sm">
        {showBreakdown && (
          <>
            <p className="flex justify-between">
              <span className="text-muted">Subtotal</span>
              <span className="font-semibold">{money(order.subtotal ?? order.total)}</span>
            </p>
            <p className="flex justify-between">
              <span className="text-muted">Shipping</span>
              <span className="font-semibold">{money(order.delivery_fee ?? 0)}</span>
            </p>
          </>
        )}
        <p className={showBreakdown ? 'mt-1 flex justify-between border-t border-line pt-2' : 'flex justify-between'}>
          <span className="font-bold">Total ({order.total_pieces} pcs)</span>
          <span className="font-extrabold text-brand-600">{money(order.total)}</span>
        </p>
      </div>
    </Card>
  )
}
